import { ITicketsBalances } from '../model/user-info.interface';
import { setFragments } from '../redux/draws';
import { setUserBalance, setUserTickets } from '../redux/user';
import { addNotifications } from '../redux/notifications';
import { reachYMGoal, YMGoals } from '../components/App/YandexMetrika';

/**
 * Команды, которые бэкенд может прислать клиенту в поле `clientAction`.
 * Выполняются на фронте после успешного ответа API.
 */
export enum ClientCommand {
    /** Отправить цель в Яндекс.Метрику за завершение N-го туториального челленджа. */
    ReachYandexMetriaGoal = 1,
}

/**
 * Описание команды из ответа API.
 * `param1` зависит от `command` (для ReachYandexMetriaGoal — номер челленджа).
 */
export interface IClientAction {
    command: ClientCommand;
    param1?: string;
}

/** Минимальный контракт стора: нужен только `dispatch` для обновления Redux. */
type StoreLike = {
    dispatch: (action: unknown) => void;
};

/**
 * Разбирает служебные поля ответа API и применяет их к приложению.
 * Вызывается из response-interceptor после каждого успешного запроса.
 *
 * Ожидаемые поля в `data` (все опциональны):
 * - `notifications` — список уведомлений;
 * - `updatedBalance` — баланс и/или билеты;
 * - `updatedFragments` — число фрагментов коллекции;
 * - `clientAction` — команда для клиента (например, цель Метрики).
 *
 * Вынесено из HTTP-клиента, чтобы слой транспорта не знал про Redux и Метрику.
 *
 * @param data - Тело ответа API.
 * @param store - Redux-стор (или `null`, если ещё не привязан).
 */
export const processApiResponseData = (data: any, store: StoreLike | null | undefined): void => {
    if (!data || !store) {
        return;
    }

    processNotifications(data.notifications, store);
    processBalanceData(data.updatedBalance, store);
    processFragmentsData(data.updatedFragments, store);
    processClientAction(data.clientAction);
};

/** Кладёт пришедшие уведомления в стор уведомлений. */
const processNotifications = (notifications: any, store: StoreLike): void => {
    if (notifications && notifications.length > 0) {
        store.dispatch(addNotifications(notifications));
    }
};

/**
 * Обновляет баланс и билеты из `updatedBalance`.
 * В объекте могут быть и `balance`, и поля билетов — обрабатываются независимо.
 */
const processBalanceData = (updatedBalance: any, store: StoreLike): void => {
    if (!updatedBalance || typeof updatedBalance !== 'object') {
        return;
    }

    if (updatedBalance.balance !== undefined) {
        store.dispatch(setUserBalance({ balance: updatedBalance.balance }));
    }

    const tickets = extractTickets(updatedBalance);
    if (tickets && Object.keys(tickets).length > 0) {
        store.dispatch(setUserTickets(tickets));
    }
};

/** Пишет новое число фрагментов, если бэкенд прислал число. */
const processFragmentsData = (updatedFragments: any, store: StoreLike): void => {
    if (typeof updatedFragments === 'number') {
        store.dispatch(setFragments(updatedFragments));
    }
};

/**
 * Выполняет команду клиента из ответа.
 * Ошибки выполнения глотаются с логом — ответ API уже успешен, UI из-за Метрики не роняем.
 */
const processClientAction = (action: IClientAction | undefined): void => {
    if (!action) {
        return;
    }

    switch (action.command) {
        case ClientCommand.ReachYandexMetriaGoal:
            try {
                const count = +(action.param1!);
                const key = `CompleteTutorialChallenges${count}` as keyof typeof YMGoals;
                const goal = YMGoals[key];

                if (goal) {
                    reachYMGoal(goal);
                }
            } catch (error: unknown) {
                console.error(`Fail to execute client action ${JSON.stringify(action)}`);
            }
            break;
    }
};

/**
 * Достаёт из `updatedBalance` только поля билетов (всё кроме `balance`).
 * @returns Объект билетов или `null`, если числовых полей нет.
 */
const extractTickets = (balanceData: any): ITicketsBalances | null => {
    if (!balanceData || typeof balanceData !== 'object') {
        return null;
    }

    const { balance, ...tickets } = balanceData;

    const hasValidTickets = Object.values(tickets).some(
        (value) => typeof value === 'number',
    );

    return hasValidTickets ? (tickets as ITicketsBalances) : null;
};
